import React, { ReactNode, useEffect, useState } from 'react'
import { EditableField } from 'src/components/Reusable/Forms/EditableField'
import { StyledTextInput } from 'src/components/Reusable/Inputs/TextInput/styles'
import { ShowPageOwnerDropdown } from 'src/components/Reusable/ShowPageOwnerDropdown'
import { ShowPageAssigneesField } from 'src/components/Reusable/ShowPageAssigneesField'
import { useAssigneeDisplayNames } from 'src/components/Reusable/AssigneeDisplay'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  ShowPageDetailSection,
  ShowPageDetailLabel,
  ShowPageDetailValue,
} from './BaseComponents'

// Shared "detail field" wrappers for show pages: each renders a
// ShowPageDetailSection + label + inline-editable value (EditableField), owning
// its own edit/pending state so the caller only supplies the value + an
// onSave callback.

const DetailLabel = ({
  label,
  tooltip,
}: {
  label: string
  tooltip?: ReactNode
}): JSX.Element =>
  tooltip ? (
    <ShowPageDetailLabel
      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
    >
      {label}
      {tooltip}
    </ShowPageDetailLabel>
  ) : (
    <ShowPageDetailLabel>{label}</ShowPageDetailLabel>
  )

// A single-line text field that hover-reveals a pencil and edits inline.
export const EditableTextDetailField = ({
  label,
  tooltip,
  value,
  placeholder,
  disabled = false,
  onSave,
}: {
  label: string
  tooltip?: ReactNode
  value: string
  placeholder: string
  disabled?: boolean
  onSave: (value: string) => Promise<void>
}): JSX.Element => {
  const [draft, setDraft] = useState(value)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    setDraft(value)
  }, [value])

  return (
    <ShowPageDetailSection hasEditableValue>
      <DetailLabel label={label} tooltip={tooltip} />
      <EditableField
        fullWidth
        disabled={disabled}
        renderView={() =>
          value ? (
            <ShowPageDetailValue>{value}</ShowPageDetailValue>
          ) : (
            <ShowPageDetailValue isDisabled>{placeholder}</ShowPageDetailValue>
          )
        }
        isEditing={isEditing}
        onEdit={() => {
          setDraft(value)
          setIsEditing(true)
        }}
        onSave={async () => {
          await onSave(draft)
          setIsEditing(false)
        }}
        onCancel={() => {
          setDraft(value)
          setIsEditing(false)
        }}
      >
        <StyledTextInput
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(e.target.value)
          }
          showError={false}
        />
      </EditableField>
    </ShowPageDetailSection>
  )
}

// Owner field: view shows the resolved display name (or "Unassigned"); edit uses
// the shared owner dropdown. onSave receives the email ('' when unassigned).
export const EditableOwnerField = ({
  label = 'Owner',
  ownerEmail,
  disabled = false,
  onSave,
}: {
  label?: string
  ownerEmail: string
  disabled?: boolean
  onSave: (email: string) => Promise<void>
}): JSX.Element => {
  const { displayName } = useAssigneeDisplayNames()
  const [isEditing, setIsEditing] = useState(false)
  const [pending, setPending] = useState<string | null>(null)

  const handleSave = async () => {
    if (pending !== null) {
      await onSave(pending === 'Unassigned' ? '' : pending)
    }
    setIsEditing(false)
  }

  return (
    <ShowPageDetailSection>
      <ShowPageDetailLabel>{label}</ShowPageDetailLabel>
      <EditableField
        disabled={disabled}
        renderView={() => (
          <ShowPageDetailValue isDisabled={!ownerEmail}>
            {ownerEmail ? displayName(ownerEmail) : 'Unassigned'}
          </ShowPageDetailValue>
        )}
        isEditing={isEditing}
        onEdit={() => {
          setPending(ownerEmail)
          setIsEditing(true)
        }}
        onSave={handleSave}
        onCancel={() => {
          setPending(null)
          setIsEditing(false)
        }}
      >
        <ShowPageOwnerDropdown
          ownerEmail={pending ?? ownerEmail}
          hideLabel
          onSave={async (email) => setPending(email)}
        />
      </EditableField>
    </ShowPageDetailSection>
  )
}

// Assignees field: view shows the first assignee + "+ N more" (all names in a
// tooltip); edit uses the shared multi-select assignees field.
export const EditableAssigneesField = ({
  label = 'Assignees',
  modelId,
  assigneeEmails,
  disabled = false,
  onSave,
}: {
  label?: string
  modelId: string
  assigneeEmails: string[]
  disabled?: boolean
  onSave: (emails: string[]) => Promise<void>
}): JSX.Element => {
  const { displayName } = useAssigneeDisplayNames()
  const [isEditing, setIsEditing] = useState(false)
  const [pending, setPending] = useState<string[] | null>(null)

  const handleSave = async () => {
    if (pending !== null) {
      await onSave(pending)
    }
    setIsEditing(false)
  }

  return (
    <ShowPageDetailSection>
      <ShowPageDetailLabel>{label}</ShowPageDetailLabel>
      <EditableField
        disabled={disabled}
        renderView={() =>
          assigneeEmails.length > 0 ? (
            <Tooltip
              id={`assignees-tooltip-${modelId}`}
              placement="top"
              tooltipBody={assigneeEmails.map(displayName).join(', ')}
            >
              <ShowPageDetailValue>
                {displayName(assigneeEmails[0])}
                {assigneeEmails.length > 1 &&
                  ` + ${assigneeEmails.length - 1} more`}
              </ShowPageDetailValue>
            </Tooltip>
          ) : (
            <ShowPageDetailValue isDisabled>Unassigned</ShowPageDetailValue>
          )
        }
        isEditing={isEditing}
        onEdit={() => {
          setPending(assigneeEmails)
          setIsEditing(true)
        }}
        onSave={handleSave}
        onCancel={() => {
          setPending(null)
          setIsEditing(false)
        }}
      >
        <ShowPageAssigneesField
          modelId={modelId}
          assigneeEmails={pending ?? assigneeEmails}
          hideLabel
          onSave={async (emails) => setPending(emails)}
        />
      </EditableField>
    </ShowPageDetailSection>
  )
}
